import { buildPartnerLayoutTree, filterDisplayTreeRoots } from './layoutTreeUtils';

/** Period vs all-time counters from one normalized listings-summary row. */
export function listingMetricsForScope(listing, scope = 'period') {
  if (!listing) return null;
  if (scope === 'lifetime') {
    const shares = listing.shareCountTotal ?? listing.shareCount ?? listing.shares ?? 0;
    const downloads =
      listing.downloadCountTotal ?? listing.downloadCount ?? listing.downloads ?? 0;
    return {
      inquiryCount: listing.inquiryCountTotal ?? listing.inquiryCount ?? 0,
      galleryViews: listing.galleryViewsTotal ?? listing.galleryViews ?? 0,
      sessions: listing.sessionsTotal ?? listing.sessions ?? 0,
      shuffles: listing.shufflesTotal ?? listing.shuffles ?? 0,
      shares,
      downloads,
      shareCount: shares,
      downloadCount: downloads,
    };
  }
  const shares = listing.shareCount ?? listing.shares ?? 0;
  const downloads = listing.downloadCount ?? listing.downloads ?? 0;
  return {
    inquiryCount: listing.inquiryCount ?? 0,
    galleryViews: listing.galleryViews ?? 0,
    sessions: listing.sessions ?? 0,
    shuffles: listing.shuffles ?? 0,
    shares,
    downloads,
    shareCount: shares,
    downloadCount: downloads,
  };
}

/** Count listing-layout links that point at a layout uuid. */
export function countLinkedPlans(listingLinks) {
  if (!Array.isArray(listingLinks) || listingLinks.length === 0) return 0;
  const seen = new Set();
  listingLinks.forEach((link) => {
    const uuid = link?.layoutUuid;
    if (uuid) seen.add(String(uuid));
  });
  return seen.size;
}

/**
 * Top-level linkable floor plans in the loaded catalog that have no listing link.
 * Uses the same tree + display rules as FloorPlanLibrarySection so the subtitle
 * count matches the Not Linked card list. Returns null when catalog has not loaded.
 */
export function countUnlinkedPlansInCatalog(catalogLayouts, listingLinks) {
  if (!Array.isArray(catalogLayouts)) return null;
  const linkedUuids = new Set();
  (Array.isArray(listingLinks) ? listingLinks : []).forEach((link) => {
    const uuid = link?.layoutUuid;
    if (uuid) linkedUuids.add(String(uuid));
  });
  const displayRoots = filterDisplayTreeRoots(buildPartnerLayoutTree(catalogLayouts));
  return displayRoots.filter((plan) => plan?.uuid && !linkedUuids.has(String(plan.uuid))).length;
}

/** Aggregate listings-summary rows for the summary band. */
export function rollupLinkedListings(listings, { scope = 'period' } = {}) {
  if (!Array.isArray(listings) || listings.length === 0) {
    return {
      count: 0,
      inquiries: 0,
      opens: 0,
      sessions: 0,
      shuffles: 0,
      shares: 0,
      downloads: 0,
    };
  }
  return listings.reduce(
    (acc, listing) => {
      const metrics = listingMetricsForScope(listing, scope);
      return {
        count: acc.count + 1,
        inquiries: acc.inquiries + (metrics?.inquiryCount ?? 0),
        opens: acc.opens + (metrics?.galleryViews ?? 0),
        sessions: acc.sessions + (metrics?.sessions ?? 0),
        shuffles: acc.shuffles + (metrics?.shuffles ?? 0),
        shares: acc.shares + (metrics?.shares ?? 0),
        downloads: acc.downloads + (metrics?.downloads ?? 0),
      };
    },
    {
      count: 0,
      inquiries: 0,
      opens: 0,
      sessions: 0,
      shuffles: 0,
      shares: 0,
      downloads: 0,
    }
  );
}

/** Index listings-summary rows by layoutUuid with customer_ref fallback. */
export function indexListingsSummaryByLayout(listings) {
  const out = new Map();
  if (!Array.isArray(listings)) return out;
  listings.forEach((listing) => {
    if (listing?.layoutUuid) {
      out.set(String(listing.layoutUuid), listing);
    } else if (listing?.customer_ref) {
      out.set(`ref:${listing.customer_ref}`, listing);
    }
  });
  return out;
}

export function lookupListingSummaryForLink(listingsByKey, linkRecord) {
  if (!listingsByKey || !linkRecord) return null;
  const uuid = linkRecord?.layoutUuid;
  if (uuid && listingsByKey.has(String(uuid))) {
    return listingsByKey.get(String(uuid));
  }
  const ref = linkRecord?.customer_ref;
  if (ref && listingsByKey.has(`ref:${ref}`)) {
    return listingsByKey.get(`ref:${ref}`);
  }
  return null;
}
